<template>
    <div id="app" :class="{AppHolder:1}">
        <ToastNotification :message="toastMessage" :background-color="toastMessageColor"/>

        <!-- CHECKING AUTH -->

        <template v-if="show_loadscreen">
            <div class="LoadingPanel">
                <img src="/img/submitting-grey.svg" alt="Loading...">
                <p>{{ welcome_message }}</p>
            </div>
        </template>

        <!-- STANDARD LAYOUT -->

        <template v-else-if="layout=='STANDARD'">

            <div class="AppHolder">

                <div class="AppTop">
                    <div class="maxwide">
                        <div class="logo">
                            <a :href="provider.subscriber_home_url">
                                <img :src="provider.subscriber_logo_url" :alt="provider.name">
                            </a>
                        </div>

                        <div class="profile">
                            <div class="identity">
                                <div v-if="user" class="name">{{ user.first_name }} {{ user.last_name }}</div>
                                <div v-if="account" class="account">Account {{
                                        account.id.toString().replace(/\d{4}(?=.)/g, '$& ')
                                    }}
                                </div>
                            </div>

                            <button @click="logoutClick">Logout&nbsp;&nbsp;&times;</button>
                        </div>
                    </div>
                </div>


                <div class="AppMain">
                    <div class="maxwide">
                        <div :class="{AppSidebar:1,open:nav_open}">

                            <nav class="AppNav" data-cy="portal-nav">
                                <router-link :to="{name:'home'}">Account</router-link>
                                <router-link v-if="show_referral_screen" :to="{name:'referrals'}">Referrals</router-link>
                                <router-link :to="{name:'billing'}">Billing</router-link>
                                <router-link :to="{name:'settings'}">Preferences</router-link>
                                <a :href="provider.subscriber_support_url">Support</a>
                            </nav>

                            <button class="menu_button" @click="nav_open=!nav_open">Menu</button>

                        </div>

                        <div v-if="nav_open" class="nav_shield" @click="nav_open=!nav_open"></div>

                        <div class="AppSupport">

                            <div class="prompt">Need help? 😊</div>

                            <div class="option">
                                <div class="title">Call us</div>
                                <div class="text"><a :href="'tel:'+provider.subscriber_support_phone">{{
                                        formatPhoneNumber(provider.subscriber_support_phone)
                                    }}</a></div>
                            </div>

                            <div class="option">
                                <div class="title">Email us</div>
                                <div class="text"><a
                                    :href="'mailto:'+provider.subscriber_support_email">{{
                                        provider.subscriber_support_email
                                    }}</a>
                                </div>
                            </div>

                        </div>

                        <div class="AppPage">
                            <div v-if="page_error">Error loading page</div>
                            <router-view v-else v-slot="{ Component }">
                              <component :is="Component" :codes="codes" />
                            </router-view>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- MINIMAL -->

        <template v-else-if="layout=='MINIMAL'">
            <div v-if="page_error">Error loading page</div>
            <router-view v-else/>
        </template>

        <AppOverlays/>

    </div>
</template>


<script>

import {ref, toRaw, watchEffect} from "vue";
import {useRoute} from 'vue-router';

import useActiveProviderStore from "../../stores/ActiveProviderStore";
import useAuthStore from "../../stores/AuthStore";
import AppOverlays from "../../components/AppOverlays";
import ToastNotification from "../../components/ToastNotification";

export default {

    components: {
        AppOverlays,
        ToastNotification
    },

    setup() {
        const page_error = ref(false);
        const {user, account, is_logged_in, loading, logout, initialised: user_initialised} = useAuthStore();
        const {provider, initialised: provider_initialised} = useActiveProviderStore();

        const route = useRoute();
        let toastMessage = ref("")
        let codes = ref(null);
        let toastMessageColor = ref('green');
        let referralKey = ref(1);
        let show_referral_screen = ref(null);

        watchEffect(() => {
            if (!isNaN(user.value.id)) {
                axios.get(`/referrer/get-codes/${user.value.id}`).then(res => {
                    codes.value = res.data.codes;
                })
            }
        })

        return {
            layout: ref((route.meta || {}).layout || 'STANDARD'),
            page_error,
            nav_open: ref(true),
            provider,
            is_logged_in,
            user,
            account,
            logout,
            loading,
            user_initialised,
            provider_initialised,
            is_public_page: ref(!!(route.meta || {}).public),
            welcome_message: ref('Hi there!'),
            pageHitTimestamp: ref((new Date()).getTime()),
            codes,
            toastMessage,
            toastMessageColor,
            referralKey,
            show_referral_screen
        };
    },

    watch: {
        '$route': {
            handler(route) {
                this.layout = (route.meta || {}).layout || 'STANDARD';
                this.nav_open = false;
                this.is_public_page = !!(route.meta || {}).public;
            },
        }
    },

    beforeMount() {
        this.welcome_message = this.randomLoadingMessage();
    },

    computed: {
        show_loadscreen() {
            return !(this.provider_initialised && (this.is_public_page || this.user_initialised));
        },
        show_referral_screen() {
            return !!(this.user.referrer_account?.id || this.user.referee_account?.id);
        }
    },

    methods: {
        logoutClick() {
            this.logout().then(() => {
                if (this.provider.subscriber_home_url) {
                    window.location.href = this.provider.subscriber_home_url;
                } else {
                    this.$router.push({name: 'Login'});
                }
            });
        },

        formatPhoneNumber(x) {
            return x.replace('+44', '0').replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');
            ;
        },

        randomLoadingMessage() {

            const options = [
                "Loading...",
                "Welcome!",
                "Hi there!",
            ];

            return options[Math.floor(Math.random() * options.length)];
        },
        redirectOrCreate(){
            if(!this.codes.length) {
                const  { email, first_name, last_name }  =  toRaw(this.user);
                let register_payload = { email, first_name, last_name, type: "", first_time_in_referral: true , providerId: this.provider.id}
                axios({
                    url: 'referrer/create',
                    method: 'POST',
                    data: register_payload
                }).then(() => {
                    this.referralKey++;
                    this.$router.push({name:'referrals'})
                }).catch(() => {
                    this.toastMessage = "Account could not be created";
                    this.toastMessageColor = "red";
                    this.referralKey++;
                })

            }else{
                this.$router.push({name:'referrals'})
            }

        }
    },
}
</script>

<style lang="scss" scoped>

.maxwide {
    margin-right: auto;
    margin-left: auto;
    max-width: 1200px;
}


.AppHolder {
    min-height: 100vh;
}

.AppTop {
    background: linear-gradient(45deg, #fafafa, #f2f2f2);
    border-bottom: 1px solid #CCC;
    padding: 0 2em;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;

    .maxwide {
        display: flex;
        justify-content: space-between;
        height: 80px;
        align-items: center;
    }

    .logo {
        a {
            display: block;

            img {
                max-width: 150px;
                max-height: 50px;
                height: auto;
                width: auto;
            }
        }
    }

    .profile {
        font-size: 0.8em;
        display: flex;
        align-items: center;

        .identity {
            margin-right: 2em;
            text-align: right;

            .account {
                font-size: 0.9em;
            }

            .name {
                font-weight: bold;
            }
        }

        @media all and (max-width: 500px) {
            .identity {
                display: none;
            }
        }

        button {
            border-radius: 1000px;
            background: #EEE;
            color: #777;
            border: 1px solid #CCC;
            padding: 0.5em 1.2em;

            &:hover {
                background: #e4e4e4;
                border: 1px solid #888;
                color: 1px solid #333;
            }
        }
    }
}


.AppMain {
    padding: 130px 2em 5em;

    & > .maxwide {
        display: flex;
    }
}


.AppSupport {
    display: none;
    $offset: 1.5em;
    position: fixed;
    left: 0;
    bottom: $offset;
    width: 18em-2*$offset;
    background: #f5f5f5;
    border-radius: .5em;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    padding: 1em 1.2em 1.5em;
    z-index: 9001;
    // box-shadow: 3px 7px 30px rgba(0,0,0,.1);

    .prompt {
        font-weight: bold;
        margin-bottom: .8em;
        font-size: 1.2em;
    }

    .option {
        & + .option {
            margin-top: 1em;
        }

        .text {
            font-weight: bold;

            a {
                text-decoration: none;
            }
        }
    }

    @media all and (max-width: 1000px) {
        display: none;
    }
}

.AppSidebar {
    bottom: 0;
    flex: 16em 0 0;
    padding-right: 4em;

    .AppNav {
        // border:1px solid rgb(214, 214, 214);
        border-right: none;
        border-bottom-left-radius: 0.5em;
        border-top-left-radius: 0.5em;
        position: sticky;
        top: 0px;
        margin-top: -110px;
        padding-top: 110px;
        border-right: 1px solid #EEE;
        padding-bottom: 50px;

        a, button {
            display: block;
            padding: 0.7em 1em;
            text-decoration: none;
            border-radius: 50em;
            text-align: left;
            border-bottom-right-radius: 0;
            border-top-right-radius: 0;
            margin-bottom: 0.2em;

            &:hover {
                background: rgba(0, 0, 0, 0.03);
            }

            &.router-link-exact-active {
                background: #FFF;
                background: rgba(0, 0, 0, 0.02);
                border-color: #CCC;
                position: relative;
                margin-right: -1px;
                border-right: 3px solid #CCC;
                color: #000;
            }

        }

    }

    .menu_button {
        display: none;
    }


}

@media all and (max-width: 950px) {

    .nav_shield {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10000 - 20;
        background: rgba(0, 0, 0, 0.1);
    }
    .AppSidebar {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 10000 - 10;
        width: 16em;
        left: -16em;
        background: #FFF;
        padding: 0;
        max-width: 100%;
        box-shadow: 0 0 50px rgba(0, 0, 0, 0.1);
        transition: left 0.2s ease;

        .menu_button {
            display: block;
            position: absolute;
            left: 100%;
            top: 80px;
            line-height: 2.2em;
            padding: 0 0.8em;
            border: 0px;
            background: #04A6AC;
            color: #FFF;

            &::after {
                content: "\203A";
                margin-left: 0.5em;
                opacity: 0.5;
            }

            // border-top-right-radius: 500em;
            border-bottom-right-radius: 0.5em;

        }

        .AppNav {
            margin-top: 0;
        }

        &.open {
            left: 0;

            .menu_button {
                &::after {
                    content: "\2039";
                }
            }
        }

        @media all and (max-width: 950px) {

        }
    }
}


.AppPage {
    background: #FFF;
    max-width: 900px;
    flex-grow: 1;
}

.AppBar {
    background: #222;
    color: #FFF;
    position: relative;
    z-index: 10000;

    .centered {
        display: flex;
        justify-content: space-between;
    }

    .actions {
        display: flex;

        a,
        button {
            flex: 1;
            display: block;
            border: none;
            background: none;
            color: #FFF;
            padding: 10px;
            text-decoration: none;

            &:hover {
                background: rgba(0, 0, 0, 0.5);
            }
        }
    }
}


.LoadingPanel {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    img {
        height: 1.5em;
    }

    p {
        font-size: 0.8em;
        color: #888;
    }
}
</style>
